import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

// MUI
import DescriptionIcon from '@mui/icons-material/Description';

// Lightbox
import { Lightbox, SlideImage } from 'yet-another-react-lightbox';
import { Zoom, Download } from 'yet-another-react-lightbox/plugins';
import 'yet-another-react-lightbox/styles.css';

// Custom
import MapCardActionButton from '@/ui/MapCard/MapCardActionButton';
import useApi from '@/lib/api/useApi';
import { saveFile } from '@/lib/fileControlUtility';
import LoaderContext, { LoaderContextType } from '@/context/LoaderContext/LoaderContext';

// Types
import { TCustomGFIDKPProps } from '@/@types/views/map';
import { DCFile } from '@/@types/lib/dataController';

const TerPotrosacDokumentiButton = (props: TCustomGFIDKPProps) => {
  const { record } = props;
  const { t } = useTranslation();
  const { get } = useApi();
  const { toggleLoading } = useContext(LoaderContext) as LoaderContextType;

  const [lightboxOpen, setLightboxOpen] = useState(false);

  const kupacId = useMemo(() => record?.kupac_id ?? record?.id ?? null, [record]);

  const {
    data: documents = [],
    isFetching,
    refetch,
  } = useQuery<DCFile[]>({
    queryKey: ['ter-potrosac-dokumenti', kupacId],
    queryFn: async () => {
      const resp = await get(`data/ter/kupci/${kupacId}/doc`);
      return resp.success && Array.isArray(resp.data) ? (resp.data as DCFile[]) : [];
    },
    enabled: false,
    staleTime: Infinity,
  });

  const slides: SlideImage[] = useMemo(
    () =>
      documents
        .filter((doc: DCFile) => doc.type.includes('image'))
        .map((doc: DCFile) => ({ src: `data:${doc.type};base64,${doc.content}`, downloadFilename: doc.name })),
    [documents]
  );

  const handleClick = async () => {
    if (!kupacId) return;
    toggleLoading(true);
    const result = await refetch();
    toggleLoading(false);
    const docs = result.data ?? [];
    docs.filter((doc: DCFile) => !doc.type.includes('image')).forEach(saveFile);
    if (docs.some((doc: DCFile) => doc.type.includes('image'))) setLightboxOpen(true);
  };

  return (
    <>
      <MapCardActionButton startIcon={<DescriptionIcon />} onClick={handleClick} disabled={isFetching}>
        {t('map:potrosaci.dokumenti')}
      </MapCardActionButton>
      <Lightbox open={lightboxOpen} close={() => setLightboxOpen(false)} slides={slides} plugins={[Zoom, Download]} />
    </>
  );
};

export default TerPotrosacDokumentiButton;
